import { useCallback, useEffect, useState } from 'react';
import { scheduledScansApi } from '../../services/scheduledScansApi';
import type { Policy } from '../../services/policiesApi';
import { buildLiveSafetyCasePath } from '../../utils/acceptanceHierarchyNavigation';
import {
  activateMeasurement,
  extractActivatedCaseId,
  caseIdFromDefinition,
  mapStartMeasuringError,
  matchScanDefinitionForPolicy,
  presentStartMeasuring,
  type StartMeasuringPresentation,
} from './startMeasuring';

export function useStartMeasuring(args: {
  siteId: string;
  orgId?: string | null;
  policy: Policy | null;
  timezone?: string | null;
  enabled?: boolean;
  navigate: (path: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleEnabled, setScheduleEnabled] = useState<boolean | null>(null);
  const [safetyCaseId, setSafetyCaseId] = useState<string | null>(null);

  useEffect(() => {
    const policy = args.policy;
    const siteId = String(args.siteId || '').trim();
    if (!args.enabled || !policy || !siteId) {
      setScheduleEnabled(null);
      setSafetyCaseId(null);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const definitions = await scheduledScansApi.listDefinitions(siteId);
        if (cancelled) return;
        const definition = matchScanDefinitionForPolicy(definitions, policy);
        if (!definition) {
          setScheduleEnabled(null);
          setSafetyCaseId(null);
          return;
        }
        const schedules = await scheduledScansApi.listSchedules(siteId, definition.scan_definition_id);
        if (cancelled) return;
        const selected = schedules.find((item) => item.enabled) || schedules[0] || null;
        setScheduleEnabled(selected ? Boolean(selected.enabled) : null);
        setSafetyCaseId(extractActivatedCaseId(selected) || caseIdFromDefinition(definition) || null);
      } catch (cause) {
        if (!cancelled) setError(mapStartMeasuringError(cause));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [args.enabled, args.policy, args.siteId]);

  const presentation: StartMeasuringPresentation = presentStartMeasuring({
    policy: args.policy,
    scheduleEnabled,
    safetyCaseId,
  });

  const startMeasuring = useCallback(async () => {
    if (!args.policy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await activateMeasurement({
        siteId: args.siteId,
        policy: args.policy,
        timezone: args.timezone,
        api: scheduledScansApi,
      });
      args.navigate(buildLiveSafetyCasePath({
        caseId: result.caseId,
        orgId: args.orgId,
        siteId: args.siteId,
      }));
    } catch (cause) {
      setError(mapStartMeasuringError(cause));
    } finally {
      setBusy(false);
    }
  }, [args]);

  return {
    busy,
    error,
    presentation,
    safetyCaseId,
    startMeasuring,
  };
}
